import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Checkbox, FormControlLabel, Button, Switch, CircularProgress, Alert,
    Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { toast } from 'react-toastify';

import http from '../../services/http';

/**
 * Quais campos o chamado precisa ter preenchidos para ser CONCLUÍDO.
 *
 * Pontos 17 e 31 da Gisele (20/10/2026). A exigência já existia com serviço e
 * categoria chumbados; aqui ela virou escolha.
 *
 * ## O que esta tela NÃO é
 *
 * Ela não é o campo obrigatório da ABERTURA, que já existe no formulário, nem
 * o `required` dos campos personalizados logo abaixo. É outro momento: o do
 * encerramento, que é quando o dado passa a valer para relatório.
 *
 * ## A lista de campos vem da api
 *
 * E não de uma cópia aqui. Com duas listas, um campo novo no servidor ficaria
 * invisível nesta tela até alguém lembrar de acrescentá-lo nos dois lugares --
 * e ninguém lembraria, porque nada quebraria.
 *
 * ## POR PERFIL (03/11/2026)
 *
 * A Gisele pediu um nível a mais: cada perfil podendo ter a sua regra. O
 * seletor do topo escolhe QUEM se está configurando -- a empresa, que é o
 * padrão de todo mundo, ou um perfil específico.
 *
 * As duas situações que esta tela precisa manter distintas, porque desenham
 * igual e significam o oposto:
 *
 * - **o perfil não tem exceção** -> ele segue a regra da empresa;
 * - **o perfil tem exceção com a lista vazia** -> ele não exige nada.
 *
 * É por isso que existe "Voltar a seguir a empresa" como ação separada:
 * desmarcar tudo e salvar NÃO devolve o perfil ao padrão -- grava "não exige
 * nada", que é outra coisa.
 *
 * ## Quem garante
 *
 * O servidor. Esta configuração alimenta tanto o aviso da tela do chamado
 * quanto a recusa da api; a tela avisa antes, mas arrastar o cartão no kanban
 * chama a api direto, sem passar por aqui. Essa lição custou dois dias em
 * 18/10.
 */

/** O valor do seletor que representa a empresa (nenhum perfil). */
const A_EMPRESA = 'tenant';

const RegraDeConclusao = () => {
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [falhou, setFalhou] = useState(false);
    const [suportados, setSuportados] = useState({});
    const [daEmpresa, setDaEmpresa] = useState({ campos: [], exigir_de_todos_os_canais: false });
    const [perfis, setPerfis] = useState([]);
    const [selecionado, setSelecionado] = useState(A_EMPRESA);
    const [campos, setCampos] = useState([]);
    const [deTodosOsCanais, setDeTodosOsCanais] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            /*
             * `/perfis` e não `/`: a rota raiz devolve a regra de QUEM PEDE --
             * ela serve a tela do chamado. Esta tela precisa de todas, e de
             * saber quais perfis têm exceção.
             */
            const { data } = await http.get('/regra-de-conclusao/perfis');

            setSuportados(data?.suportados || {});
            setDaEmpresa(data?.tenant || { campos: [], exigir_de_todos_os_canais: false });
            setPerfis(Array.isArray(data?.perfis) ? data.perfis : []);
            setCampos(Array.isArray(data?.tenant?.campos) ? data.tenant.campos : []);
            setDeTodosOsCanais(Boolean(data?.tenant?.exigir_de_todos_os_canais));
            setSelecionado(A_EMPRESA);
            setFalhou(false);
        } catch (e) {
            // Falhar calado aqui mostraria uma lista VAZIA, que se parece com
            // "nenhum campo é obrigatório" -- e alguém salvaria por cima disso,
            // desligando a trava sem querer.
            setFalhou(true);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const perfilAberto = useMemo(
        () => (selecionado === A_EMPRESA ? null : perfis.find((p) => p.profile_id === selecionado)),
        [selecionado, perfis]
    );

    /** O perfil aberto ainda não tem exceção: ele segue a empresa. */
    const seguindoAEmpresa = Boolean(perfilAberto) && !perfilAberto.configurado;

    const trocarDeAlvo = (valor) => {
        setSelecionado(valor);

        if (valor === A_EMPRESA) {
            setCampos(daEmpresa.campos || []);
            return;
        }

        const perfil = perfis.find((p) => p.profile_id === valor);

        /*
         * O perfil sem exceção abre JÁ COM os campos da empresa, que é o que
         * vale para ele hoje. Abrir em branco faria parecer que ele não exige
         * nada -- e um "Salvar" sem pensar gravaria exatamente isso.
         */
        setCampos(perfil?.configurado ? (perfil.campos || []) : (daEmpresa.campos || []));
    };

    const alternar = (campo) => setCampos((atuais) => (
        atuais.includes(campo) ? atuais.filter((c) => c !== campo) : [...atuais, campo]
    ));

    const aplicarResposta = (data) => {
        setDaEmpresa(data?.tenant || { campos: [], exigir_de_todos_os_canais: false });
        setPerfis(Array.isArray(data?.perfis) ? data.perfis : []);
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            const corpo = { campos };

            // O alcance só existe na regra da empresa -- a api recusa se vier
            // junto de um perfil, e com razão: ele diz a QUAIS CHAMADOS a
            // regra se aplica, não o que ela exige.
            if (selecionado === A_EMPRESA) {
                corpo.exigir_de_todos_os_canais = deTodosOsCanais;
            } else {
                corpo.profile_id = selecionado;
            }

            const { data } = await http.put('/regra-de-conclusao', corpo);
            aplicarResposta(data);

            toast.success(
                selecionado === A_EMPRESA
                    ? 'Regra da empresa salva.'
                    : `Exceção de ${perfilAberto?.nome} salva.`,
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } catch (e) {
            toast.error(
                e?.response?.status === 403
                    ? 'Apenas administradores podem alterar esta regra.'
                    : (e?.response?.data?.error || 'Não foi possível salvar a regra de conclusão.'),
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Tira a exceção do perfil: ele volta a seguir a empresa.
     *
     * É ação separada porque desmarcar tudo e salvar significa OUTRA coisa --
     * "este perfil não exige nada". Sem este botão, um perfil configurado por
     * engano ficaria preso numa regra própria para sempre.
     */
    const voltarASeguirAEmpresa = async () => {
        setSalvando(true);
        try {
            const { data } = await http.delete(`/regra-de-conclusao/perfis/${selecionado}`);
            aplicarResposta(data);
            setCampos(data?.tenant?.campos || []);
            toast.success(
                `${perfilAberto?.nome} voltou a seguir a regra da empresa.`,
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } catch (e) {
            toast.error('Não foi possível remover a exceção.', { position: toast.POSITION.BOTTOM_CENTER });
        } finally {
            setSalvando(false);
        }
    };

    if (carregando) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} />
            </Box>
        );
    }

    if (falhou) {
        return (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="regra-de-conclusao-falhou">
                Não foi possível carregar a regra de conclusão. Recarregue a página antes de alterar
                qualquer coisa — salvar agora gravaria uma lista vazia por cima da configuração atual.
            </Alert>
        );
    }

    return (
        <Box data-testid="regra-de-conclusao" sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Campos obrigatórios para concluir o chamado
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                A exigência vale no momento de finalizar (Resolvido, Fechado ou Cancelado), e não a
                cada alteração — o chamado continua podendo ser trabalhado sem eles.
            </Typography>

            <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
                <InputLabel>Configurando</InputLabel>
                <Select
                    value={selecionado}
                    label="Configurando"
                    onChange={({ target }) => trocarDeAlvo(target.value)}
                    SelectDisplayProps={{ 'data-testid': 'alvo-da-regra' }}
                >
                    <MenuItem value={A_EMPRESA}>A empresa (vale para todos os perfis)</MenuItem>
                    {perfis.map((p) => (
                        <MenuItem key={p.profile_id} value={p.profile_id}>
                            {p.nome}
                            {p.configurado ? ' — exceção' : ' — segue a empresa'}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {seguindoAEmpresa && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '12.5px' }} data-testid="perfil-segue-a-empresa">
                    {perfilAberto.nome} segue a regra da empresa. Os campos abaixo já vêm marcados como
                    ela está hoje — salvar cria uma exceção só para este perfil.
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {Object.entries(suportados).map(([campo, rotulo]) => (
                    <FormControlLabel
                        key={campo}
                        sx={{ minWidth: 200 }}
                        control={(
                            <Checkbox
                                size="small"
                                checked={campos.includes(campo)}
                                inputProps={{ 'data-testid': `campo-${campo}` }}
                                onChange={() => alternar(campo)}
                            />
                        )}
                        label={<Typography sx={{ fontSize: '13px' }}>{rotulo}</Typography>}
                    />
                ))}
            </Box>

            {/*
              O alcance é da EMPRESA, e por isso some ao abrir um perfil. Por
              perfil, o mesmo chamado precisaria de classificação ou não
              dependendo de quem abriu a tela -- ele diz a QUAIS CHAMADOS a
              regra se aplica, não o que ela exige.
            */}
            {selecionado === A_EMPRESA && (
                <FormControlLabel
                    sx={{ display: 'block', mb: 1 }}
                    control={(
                        <Switch
                            size="small"
                            checked={deTodosOsCanais}
                            inputProps={{ 'data-testid': 'exigir-de-todos-os-canais' }}
                            onChange={({ target }) => setDeTodosOsCanais(target.checked)}
                        />
                    )}
                    label={(
                        <Typography sx={{ fontSize: '13px' }}>
                            Exigir também dos chamados que não vieram de conversa
                            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary', ml: 0.5 }}>
                                (atinge o histórico antigo sem classificação; deixe desligado em caso de dúvida)
                            </Typography>
                        </Typography>
                    )}
                />
            )}

            {campos.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1, fontSize: '12.5px' }} data-testid="aviso-de-lista-vazia">
                    Sem nenhum campo marcado, o chamado pode ser concluído em branco
                    {selecionado === A_EMPRESA ? '.' : ` por quem tem o perfil ${perfilAberto?.nome}.`}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                    variant="contained"
                    size="small"
                    onClick={salvar}
                    disabled={salvando}
                    data-testid="salvar-regra-de-conclusao"
                >
                    {salvando ? 'Salvando...' : 'Salvar'}
                </Button>

                {perfilAberto && perfilAberto.configurado && (
                    <Button
                        size="small"
                        onClick={voltarASeguirAEmpresa}
                        disabled={salvando}
                        data-testid="voltar-a-seguir-a-empresa"
                    >
                        Voltar a seguir a empresa
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default RegraDeConclusao;
